import type { ReactNode } from 'react';

export interface AuthShellProps {
  brand: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  /** Secondary links below the form: "Forgot password?", "Back to sign in". */
  footer?: ReactNode;
  /**
   * Pull quote on the decorative panel. Omit and the panel shows the brand
   * alone. The panel itself is hidden below `lg`.
   */
  aside?: {
    heading: ReactNode;
    body?: ReactNode;
  };
}

/**
 * Layout for everything outside the authenticated app: sign in, forgot
 * password, reset password, accept invitation.
 *
 * Two columns on a wide screen — the form on the left, a dark panel on the
 * right — collapsing to the form alone below `lg`.
 */
export function AuthShell({ brand, title, subtitle, children, footer, aside }: AuthShellProps) {
  return (
    <div className="flex min-h-svh bg-card">
      <div className="flex min-w-0 flex-1 flex-col items-center justify-center px-6 py-12">
        <div className="flex w-full max-w-sm flex-col gap-7">
          {brand}

          <div className="grid gap-1.5">
            <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
            {subtitle ? <p className="text-sm text-muted-foreground">{subtitle}</p> : null}
          </div>

          {children}

          {footer ? <div className="flex justify-center text-sm">{footer}</div> : null}
        </div>
      </div>

      <div className="relative hidden flex-[0_0_44%] items-end overflow-hidden bg-panel p-12 text-panel-foreground lg:flex">
        {/* A faint grid, fading out towards the text, so the panel has texture
            without competing with the form. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.07] [background-image:linear-gradient(to_right,currentColor_1px,transparent_1px),linear-gradient(to_bottom,currentColor_1px,transparent_1px)] [background-size:44px_44px] [mask-image:radial-gradient(ellipse_at_top_right,black,transparent_70%)]"
        />
        <div className="relative grid max-w-md gap-3">
          <h2 className="text-3xl leading-tight font-semibold tracking-tight">
            {aside?.heading ?? brand}
          </h2>
          {aside?.body ? (
            <p className="text-[15px] text-panel-foreground/65">{aside.body}</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
